// Angular
import { Injectable, computed, signal } from '@angular/core';

/* ============================================================
 * Types
 * ============================================================ */

/** Which sub-view the panel renders */
export type ChatView = 'list' | 'chat';

/** Presence/status shown next to each representative */
export type Presence = 'online' | 'away' | 'busy' | 'offline';

/** Handshake/request lifecycle */
export type ChatRequestStatus = 'idle' | 'pending' | 'accepted' | 'declined' | 'ended';

/** A representative in the list */
export interface ChatUser {
    id: string | number;
    name: string;
    presence: Presence;
}

/** The company/store context the panel is opened for */
export interface CompanyContext {
    id: number | string;
    name: string;
    part_item?: string | number;
    part_post_id?: string | number;
}

/** One chat message bubble */
export interface ChatMessage {
    id: string;
    from: 'customer' | 'rep' | 'system';
    text: string;
    ts: number; // epoch millis
}

/* ============================================================
 * Service
 * ============================================================ */

@Injectable({ providedIn: 'root' })
export class ChatPanelService {
    
    // -------- Visibility & navigation --------
    private _open = signal(false);
    private _minimized = signal(false);
    private _view = signal<ChatView>('list');

    // -------- Selections & context --------
    private _selectedUser = signal<ChatUser | null>(null);
    private _company = signal<CompanyContext | null>(null);

    // -------- Data for current company --------
    private _users = signal<ChatUser[]>([]);
    private _loadingUsers = signal(false);

    // -------- Conversation / handshake --------
    private _requestStatus = signal<ChatRequestStatus>('idle');
    private _firstReply = signal(false);
    private _messages = signal<ChatMessage[]>([]);
    private _repTyping = signal(false);

    // -------- Unread badge (no sound) --------
    private _unread = signal(0);

    /* ============================================================
     * Read API (computed)
     * ============================================================ */

    /** True if panel is open (minimized or not) */
    readonly open = computed(() => this._open());

    /** True if panel is minimized to dock */
    readonly minimized = computed(() => this._minimized());

    /** Current sub-view: 'list' | 'chat' */
    readonly view = computed(() => this._view());

    /** Selected representative (or null) */
    readonly selectedUser = computed(() => this._selectedUser());

    /** Active company context (or null) */
    readonly company = computed(() => this._company());

    /** Mapped list of representatives for the active company */
    readonly users = computed(() => this._users());

    /** Loading flag while fetching representatives */
    readonly loadingUsers = computed(() => this._loadingUsers());

    /** Server handshake status for the active chat */
    readonly requestStatus = computed(() => this._requestStatus());

    /** Whether at least one seller reply has been received (kept for extensibility) */
    readonly hasFirstReply = computed(() => this._firstReply());

    /** Current message list for the chat */
    readonly messages = computed(() => this._messages());

    /** Whether the seller is currently typing */
    readonly repTyping = computed(() => this._repTyping());
    readonly unreadCount = computed(() => this._unread());

    readonly isFullPanelVisible = computed(() => this._open() && !this._minimized());
    readonly isListView = computed(() => this.isFullPanelVisible() && this._view() === 'list');
    readonly isChatView = computed(() => this.isFullPanelVisible() && this._view() === 'chat');
    readonly companyName = computed(() => this._company()?.name ?? 'Please wait...');

    /** Allow typing as soon as seller ACCEPTS */
    readonly composerEnabled = computed(() => this._requestStatus() === 'accepted');

    /** True if a chat is ongoing (used to confirm on back/close) */
    readonly isInConversation = computed(() => {
        const s = this._requestStatus();
        return s === 'pending' || s === 'accepted';
    });

    // =============== Actions ===============
    openPanel() { this._open.set(true); this._minimized.set(false); }
    closePanel() { this._open.set(false); this._minimized.set(false); this.resetView(); this.resetConversationState(); }

    minimizePanel() { if (this._open()) this._minimized.set(true); }
    toggleMinimize() { if (!this._open()) { this.openPanel(); return; } this._minimized.update(m => !m); }

    canChat(u: ChatUser | null | undefined) { return !!u && (u.presence === 'online' || u.presence === 'away'); }

    selectUser(u: ChatUser): void {
        if (!this.canChat(u)) return;
        this._selectedUser.set(u);
        this._view.set('chat');
        this.resetConversationState();
        this.clearUnread();
    }

    restorePanel(): void {
        if (!this._open()) this._open.set(true);
        this._minimized.set(false);
        this.clearUnread();
    }

    backToList() {
        this._view.set('list');
        this._selectedUser.set(null);
        this.resetConversationState();
    }

    setCompany(ctx: CompanyContext) { this._company.set(ctx); }
    clearCompany() { this._company.set(null); }
    setUsers(list: ChatUser[]) { this._users.set(list ?? []); }
    setUsersLoading(b: boolean) { this._loadingUsers.set(b); }

    beginChatRequest() { this._requestStatus.set('pending'); this._firstReply.set(false); }
    setRequestStatus(status: ChatRequestStatus) { this._requestStatus.set(status); }
    markFirstReply() { this._firstReply.set(true); }

    // --- Messages & typing ---
    appendCustomerMessage(text: string) { this._messages.update(a => [...a, msg('customer', text)]); }
    appendRepMessage(text: string) { this._messages.update(a => [...a, msg('rep', text)]); }
    appendSystemMessage(text: string) { this._messages.update(a => [...a, msg('system', text)]); }
    clearMessages() { this._messages.set([]); }
    setRepTyping(on: boolean) { this._repTyping.set(on); }

    // --- Unread (no sound) ---
    bumpUnread() { this._unread.update(c => Math.min(99, c + 1)); }
    clearUnread() { this._unread.set(0); }

    // --- Presence (from server status frames) ---
    private mapStatusCodeToPresence(code: number): Presence {
        switch (code) {
            case 1: return 'online';
            case 2: return 'away';
            case 3: return 'busy';
            default: return 'offline';
        }
    }
    setUserPresence(id: string | number, presence: Presence): void {
        this._users.update(list => list.map(u => (u.id === id ? { ...u, presence } : u)));
        if (this._selectedUser()?.id === id) {
            this._selectedUser.update(u => (u ? { ...u, presence } : u));
        }
    }
    applyServerStatus(userId: string | number, code: number): void {
        this.setUserPresence(userId, this.mapStatusCodeToPresence(code));
    }
    readonly selectedPresence = computed<Presence | null>(() =>
        this._selectedUser()?.presence ?? null
    );

    // =============== Internals ===============
    private resetView() { this._view.set('list'); this._selectedUser.set(null); }
    private resetConversationState() {
        this._requestStatus.set('idle');
        this._firstReply.set(false);
        this._repTyping.set(false);
        this._messages.set([]);
    }
}

function msg(from: ChatMessage['from'], text: string): ChatMessage {
    return { id: cryptoId(), from, text, ts: Date.now() };
}
function cryptoId() {
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return Math.random().toString(36).slice(2);
}
